import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'
import { AlertTriangle, CheckCircle2, Loader2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState, Skeleton } from '@/components/ui/misc'
import { TraceId } from '@/components/ui/trace-id'
import { getJob, getJobs } from '@/lib/api/jobs/get-jobs'
import { translateEnum } from '@/lib/i18n'
import { cn, formatDateTime } from '@/lib/utils'

/**
 * What the language model was actually asked and what it actually returned.
 *
 * A trace id points at a log line; this points at the content, which is what
 * you need when the model returns something surprising rather than something
 * broken. Entries are kept for 48 hours — this is a debugging aid, not an archive.
 */
export function JobsPage() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()
  const openId = Number(params.get('too')) || null
  const [onlyErrors, setOnlyErrors] = useState(false)

  const jobs = useQuery({
    queryKey: ['jobs'],
    queryFn: () => getJobs(200),
    refetchInterval: 10_000, // running jobs finish while you watch
  })

  const visible = (jobs.data ?? []).filter((j) => !onlyErrors || j.status === 'error')

  return (
    <div className="flex min-h-0 flex-1">
      <section className="flex w-[520px] shrink-0 flex-col border-r border-border">
        <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
          <h2 className="text-sm font-semibold">{t('jobs.heading')}</h2>
          <span className="text-[11px] text-muted-foreground">{t('jobs.retention')}</span>
          <Button
            variant={onlyErrors ? 'default' : 'outline'}
            size="sm"
            className="ml-auto"
            onClick={() => setOnlyErrors((v) => !v)}
          >
            {t('jobs.onlyErrors')}
          </Button>
        </div>

        {jobs.isLoading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : !visible.length ? (
          <EmptyState
            title={onlyErrors ? t('jobs.noErrors') : t('jobs.empty')}
            hint={t('jobs.emptyHint')}
          />
        ) : (
          <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
            {visible.map((j) => (
              <button
                key={j.id}
                type="button"
                onClick={() => setParams((p) => {
                  const next = new URLSearchParams(p)
                  next.set('too', String(j.id))
                  return next
                })}
                className={cn(
                  'flex w-full items-start gap-2 border-b border-border/60 px-3 py-2 text-left transition-colors',
                  j.id === openId ? 'bg-accent' : 'hover:bg-muted',
                )}
              >
                <StatusIcon status={j.status} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium">
                    {translateEnum('jobs.kind', j.kind)}
                    {j.document_title && (
                      <span className="font-normal text-muted-foreground">
                        {' · '}
                        {j.document_title}
                      </span>
                    )}
                  </p>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {j.status === 'error' ? j.error : j.result || '—'}
                  </p>
                </div>
                <div className="shrink-0 text-right text-[10px] text-muted-foreground">
                  <div className="tabular-nums">
                    {j.duration_ms != null
                      ? t('common.milliseconds', { count: j.duration_ms })
                      : '…'}
                  </div>
                  <div>{t('units.calls', { count: j.llm_calls })}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="min-w-0 flex-1">
        {openId ? (
          <JobDetailPane
            id={openId}
            onClose={() =>
              setParams((p) => {
                const next = new URLSearchParams(p)
                next.delete('too')
                return next
              })
            }
          />
        ) : (
          <EmptyState title={t('jobs.select')} hint={t('jobs.selectHint')} />
        )}
      </section>
    </div>
  )
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'error') return <AlertTriangle className="mt-0.5 size-4 shrink-0 text-danger" />
  if (status === 'running')
    return <Loader2 className="mt-0.5 size-4 shrink-0 animate-spin text-primary" />
  return <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
}

function JobDetailPane({ id, onClose }: { id: number; onClose: () => void }) {
  const { t } = useTranslation()
  const job = useQuery({ queryKey: ['job', id], queryFn: () => getJob(id) })

  if (job.isLoading) {
    return (
      <div className="space-y-2 p-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }
  if (job.error || !job.data) return <EmptyState title={t('jobs.notFound')} />

  const j = job.data
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold">{translateEnum('jobs.kind', j.kind)}</h3>
          <Badge variant={j.status === 'error' ? 'danger' : j.status === 'ok' ? 'success' : 'default'}>
            {j.status}
          </Badge>
          <TraceId id={j.trace_id} />
          <Button variant="ghost" size="icon-sm" className="ml-auto" onClick={onClose}>
            <X />
          </Button>
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          {j.document_title && `${j.document_title} · `}
          {j.model} · {t('units.calls', { count: j.llm_calls })} ·{' '}
          {j.duration_ms != null ? t('common.milliseconds', { count: j.duration_ms }) : '—'} ·{' '}
          {j.started_at && formatDateTime(j.started_at)}
        </p>
        {j.error && (
          <p className="mt-2 rounded border border-danger/30 bg-danger/10 p-2 text-[11px] text-danger">
            {j.error}
          </p>
        )}
      </div>

      <div className="scrollbar-thin min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        <Block title={t('jobs.blockPrompt')} text={j.prompt} />
        <Block title={t('jobs.blockInput')} text={j.input_sample} />
        <Block title={t('jobs.blockOutput')} text={j.output_sample} />
        {j.result && <Block title={t('jobs.blockResult')} text={j.result} />}
      </div>
    </div>
  )
}

function Block({ title, text }: { title: string; text: string }) {
  const { t } = useTranslation()
  if (!text) return null
  return (
    <div>
      <div className="mb-1 flex items-center gap-2">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </span>
        <span className="text-[10px] text-muted-foreground/70">
          {t('common.characters', { count: text.length })}
        </span>
      </div>
      <pre className="scrollbar-thin max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted/40 p-2 font-mono text-[11px] leading-relaxed">
        {text}
      </pre>
    </div>
  )
}
